/* 术语扫描：把页面文字里的术语包上 .gloss 标记，供全局浮层解释。
 *
 * 难点：怎么改 Vue 管的 DOM 而不和它打架
 * =====================================
 * Vue 记着每个文本节点的引用，更新时直接 `node.nodeValue = 新值`。
 * 如果我们把一个文本节点拆成三段塞进 span，Vue 记的还是其中一段 ——
 * 它下次写入时那段已经脱离文档，写了等于没写，界面从此定格在旧值。
 * 对一个每 2 秒刷新报价的控制台，这是不能接受的失败方式。
 *
 * 解法：留一个"活的原件"
 * ---------------------
 * 不销毁原文本节点，而是把它藏进隐藏容器里，**仍然挂在文档中**：
 *
 *     <span class="gloss-wrap">
 *       <span class="gloss-src">预计滑点</span>   ← 原节点，display:none
 *       <span class="gloss-view">预计<span class="gloss">滑点</span></span>
 *     </span>
 *
 * 于是：Vue 的引用依然有效，它照常写入；写入会触发 characterData
 * 变动，我们收到后按新文本重绘 view。数据永远是对的，标记自己跟上。
 *
 * 正因为不会再写坏东西，扫描范围可以放开到整页 —— 术语在这个项目里
 * 散落在 <b>、<label>、说明段落各处，靠白名单根本框不住。
 */

import { MATCHERS } from '@/glossary'

/** 这些容器里的文字不碰：可编辑的、代码、图形。 */
const SKIP_TAGS = new Set([
  'INPUT',
  'TEXTAREA',
  'SELECT',
  'OPTION',
  'SCRIPT',
  'STYLE',
  'SVG',
  'PATH',
  'CODE',
  'PRE',
])

/** 已处理过的结构，以及作者显式排除的区域。 */
const SKIP_CLOSEST = '.gloss, .gloss-src, .gloss-view, [data-no-term], [contenteditable]'

const isAscii = (s) => /^[\x20-\x7e]+$/.test(s)
const isWordChar = (ch) => ch !== undefined && /[A-Za-z0-9_]/.test(ch)

/** 找出文本里互不重叠的术语位置。MATCHERS 已按长度倒序，命中即最长。 */
export function findMatches(text) {
  const out = []
  let i = 0
  while (i < text.length) {
    let hit = null
    for (const [lit, key] of MATCHERS) {
      if (!text.startsWith(lit, i)) continue
      // 英文术语要卡词边界，否则 "bps" 会在 "bpsLimit" 里命中
      if (isAscii(lit) && (isWordChar(text[i - 1]) || isWordChar(text[i + lit.length]))) {
        continue
      }
      hit = [lit, key]
      break
    }
    if (hit) {
      out.push({ start: i, end: i + hit[0].length, key: hit[1] })
      i += hit[0].length
    } else {
      i += 1
    }
  }
  return out
}

/** 按文本重绘可见层。记下源文本，下次没变就不用重绘。 */
function renderView(view, text) {
  view.textContent = ''
  let cursor = 0
  for (const h of findMatches(text)) {
    if (h.start > cursor) {
      view.appendChild(document.createTextNode(text.slice(cursor, h.start)))
    }
    const span = document.createElement('span')
    span.className = 'gloss'
    span.dataset.term = h.key
    span.tabIndex = 0
    span.textContent = text.slice(h.start, h.end)
    view.appendChild(span)
    cursor = h.end
  }
  if (cursor < text.length) {
    view.appendChild(document.createTextNode(text.slice(cursor)))
  }
  view.dataset.src = text
}

function wrapTextNode(node) {
  const text = node.nodeValue
  if (!text || !text.trim()) return
  if (!findMatches(text).length) return

  const parent = node.parentNode
  if (!parent) return

  const wrap = document.createElement('span')
  wrap.className = 'gloss-wrap'
  const src = document.createElement('span')
  src.className = 'gloss-src'
  const view = document.createElement('span')
  view.className = 'gloss-view'

  parent.replaceChild(wrap, node)
  src.appendChild(node) // 原节点移进隐藏层，仍在文档里 —— 这是全部关键
  wrap.appendChild(src)
  wrap.appendChild(view)
  renderView(view, text)
}

/** Vue 改过原文本的，把可见层同步过来。 */
function syncWrapped(root) {
  root.querySelectorAll?.('.gloss-wrap').forEach((wrap) => {
    const src = wrap.firstElementChild
    const view = wrap.lastElementChild
    if (!src || !view || src === view) return
    const text = src.textContent
    if (view.dataset.src === text) return
    // 原文本被清空（Vue 用 textContent 整体重写父节点时会这样）——
    // 整个包装失去意义，还原成纯文本让下一轮重新识别
    if (!text) return wrap.remove()
    renderView(view, text)
  })
}

/** 扫描 root 内（含自身）的文字并包上标记。幂等，可反复调用。 */
export function scan(root) {
  if (!root || root.nodeType !== 1) return
  if (root.closest?.(SKIP_CLOSEST)) return

  syncWrapped(root)

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT
      const p = node.parentElement
      if (!p) return NodeFilter.FILTER_REJECT
      if (SKIP_TAGS.has(p.tagName)) return NodeFilter.FILTER_REJECT
      if (p.closest(SKIP_CLOSEST)) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  // 先收集再改：边遍历边改 DOM 会让 TreeWalker 漏节点
  const nodes = []
  let n
  while ((n = walker.nextNode())) nodes.push(n)
  nodes.forEach(wrapTextNode)
}

// --- 自动扫描 --------------------------------------------------------------

/* 调度用 setTimeout，不用 requestAnimationFrame。
 *
 * 这是踩出来的：标签页不可见时浏览器**完全不触发 rAF**，回调永远排不上，
 * "已排队"标志就永久卡住，此后所有变动全被丢掉。交易控制台经常开在
 * 后台标签或副屏上，那正是最不能失效的场景。setTimeout 在隐藏标签页里
 * 只会被降频（最低约 1 秒一次），不会停摆。
 */
const DEBOUNCE_MS = 60

let observer = null
let timer = null
let wrapping = false
let dirty = false
let observedRoot = null

function flush() {
  timer = null
  if (!observedRoot) return
  wrapping = true
  dirty = false
  try {
    scan(observedRoot)
  } finally {
    // 解除标志放到下一个宏任务：我们自己的 DOM 改动产生的 mutation 记录
    // 是在本轮之后才派发的，立刻解除会让它们排出一次空扫描
    setTimeout(() => {
      wrapping = false
      // 扫描期间来过真实变动，补一次 —— 少了这句就会丢更新
      if (dirty) schedule()
    }, 0)
  }
}

function schedule() {
  if (wrapping) {
    dirty = true
    return
  }
  if (timer) return
  timer = setTimeout(flush, DEBOUNCE_MS)
}

/** 开始监听 root 的变化并自动扫描。重复调用会先停掉上一个。 */
export function startAutoScan(root) {
  stopAutoScan()
  if (!root) return
  observedRoot = root
  scan(root) // 先同步扫一遍，首屏不必等到第一次变动
  observer = new MutationObserver(schedule)
  observer.observe(root, { childList: true, subtree: true, characterData: true })
}

export function stopAutoScan() {
  observer?.disconnect()
  observer = null
  observedRoot = null
  if (timer) clearTimeout(timer)
  timer = null
  wrapping = false
  dirty = false
}
